// FlowsTransitLib.jsx
// Pure functions — no React. All exports assigned to window.*
// Computational backbone for Bay Flows transit visualization.

// ---------------------------------------------------------------------------
// haversine(lat1, lng1, lat2, lng2) → distance in miles
// ---------------------------------------------------------------------------
function haversine(lat1, lng1, lat2, lng2) {
  const R = 3959; // Earth radius in miles
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// ---------------------------------------------------------------------------
// findNearestStation(lat, lng, stations) → { station, distance }
// ---------------------------------------------------------------------------
function findNearestStation(lat, lng, stations) {
  let nearest = null;
  let minDist = Infinity;
  for (const station of stations) {
    const dist = haversine(lat, lng, station.lat, station.lng);
    if (dist < minDist) {
      minDist = dist;
      nearest = station;
    }
  }
  return { station: nearest, distance: minDist };
}

// ---------------------------------------------------------------------------
// dijkstraWithRoutes(sourceId, edges) → { dist, prev }
//   dist: Map<stationId, { stops, time }>   (identical shape to dijkstra())
//   prev: Map<stationId, { from, edge }>     predecessor + the edge taken
// Edges are bidirectional: { from, to, time, stops, lines?, type? }. Transfer
// edges (type:"transfer", stops:0) participate naturally — they're just edges.
// The prev map lets T10 reconstruct a station-by-station route (buildRouteSteps).
// Update rule is byte-identical to the legacy dijkstra() so dist values never
// drift for existing callers; we only additionally record the predecessor.
// ---------------------------------------------------------------------------
function dijkstraWithRoutes(sourceId, edges) {
  // Build adjacency list — carry the source edge so routes can be rebuilt.
  const adj = new Map();
  for (const edge of edges) {
    if (!adj.has(edge.from)) adj.set(edge.from, []);
    if (!adj.has(edge.to)) adj.set(edge.to, []);
    adj.get(edge.from).push({ id: edge.to, time: edge.time, stops: edge.stops, edge });
    adj.get(edge.to).push({ id: edge.from, time: edge.time, stops: edge.stops, edge });
  }

  const dist = new Map();
  const prev = new Map();
  dist.set(sourceId, { stops: 0, time: 0 });

  // Priority queue: array of { id, time, stops }, sorted by time ascending
  const queue = [{ id: sourceId, time: 0, stops: 0 }];

  while (queue.length > 0) {
    queue.sort((a, b) => a.time - b.time);
    const current = queue.shift();

    const currentBest = dist.get(current.id);
    if (current.time > currentBest.time) continue;

    const neighbors = adj.get(current.id) || [];
    for (const neighbor of neighbors) {
      const newTime = current.time + neighbor.time;
      const newStops = current.stops + neighbor.stops;
      const existing = dist.get(neighbor.id);
      if (!existing || newTime < existing.time) {
        dist.set(neighbor.id, { stops: newStops, time: newTime });
        prev.set(neighbor.id, { from: current.id, edge: neighbor.edge });
        queue.push({ id: neighbor.id, time: newTime, stops: newStops });
      }
    }
  }

  return { dist, prev };
}

// ---------------------------------------------------------------------------
// dijkstra(sourceId, edges) → Map<stationId, { stops, time }>
// Legacy contract preserved (all existing callers unchanged). Delegates to
// dijkstraWithRoutes and returns only the distance map.
// ---------------------------------------------------------------------------
function dijkstra(sourceId, edges) {
  return dijkstraWithRoutes(sourceId, edges).dist;
}

// ---------------------------------------------------------------------------
// STATION_MATCH_MILES — event↔station match radius AND safety gate (P3,
// 2026-07-16). Two jobs, one number:
//
//   MATCH RADIUS — an event snaps to its nearest station within this range
//   (matchEventsToStations below).
//
//   SAFETY GATE — the walk-distance ceiling past which /flows REFUSES the
//   event entirely: no feed row, no marker, deep links hit the N3 off-grid
//   refusal in FlowsApp. A beyond-gate venue (Shoreline Amphitheatre sits
//   ~2mi from the nearest rail) can never render here, which is the only
//   reason this surface cannot send a rider on a multi-mile midnight walk.
//   The matcher drops non-matching events silently (no else branch) — that
//   downstream refusal IS the feature, not a coverage bug.
//
// 0.75mi ≈ a 15-minute walk at the repo's one pedestrian pace
// (WALK_M_PER_MIN = 80 m/min, scrapers/build-transit.js — reference it, do
// not restate it).
//
// EXACTLY TWO readers (verified 2026-07-16; the feed and the deep-link
// inherit FlowsApp's pre-filtered eventMap and never re-match):
//   • components/FlowsApp.jsx (~:471) — builds the eventMap once
//   • lib/venue-hub-minutes.js (~:45) — VenuePage hub minutes
//
// WIDENING THIS FOR INVENTORY COVERAGE SHIPS THE HAZARD. A 2.5mi widening
// was proposed on 2026-07-16 and ruled against. Any change to this value
// needs an Atlas + Reza co-sign — the safety-gate suite in
// tests/flows-transit-lib.test.js fails loudly until it has one. See
// docs/design/2026-07-16-fable5-P3-document-the-safety-gate-prompt.md.
// ---------------------------------------------------------------------------
const STATION_MATCH_MILES = 0.75;

// ---------------------------------------------------------------------------
// matchEventsToStations(events, stations, maxMiles) → Map<key, event[]> where
// key = the nearest station's PHYSICAL identity: canonicalId ?? id (B10-2).
// A twin pair shares ONE bucket keyed by its canonical member, so every
// display surface that inherits this map (feed / map / detail / deep-link)
// renders one physical station as one entity. A canonicalId-absent stations
// snapshot (deploy/CDN skew: new JS meeting a cached pre-Block-1 data file)
// degrades to raw-id keying — the exact pre-B10 behavior — never to an
// undefined key. Each matched event gains an _stationDist property.
// ---------------------------------------------------------------------------
function matchEventsToStations(events, stations, maxMiles) {
  const result = new Map();
  for (const event of events) {
    if (event.lat == null || event.lng == null) continue;
    const { station, distance } = findNearestStation(event.lat, event.lng, stations);
    if (station && distance <= maxMiles) {
      const key = station.canonicalId ?? station.id;
      if (!result.has(key)) result.set(key, []);
      const matched = Object.assign({}, event, { _stationDist: distance });
      result.get(key).push(matched);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Constants
// IDs verified against data/stations.json
// ---------------------------------------------------------------------------
const CITY_HUBS = {
  'OAKLAND':    'bart-12th-st',
  'SF':         'bart-powell',
  'SAN JOSE':   'caltrain-diridon',
  'DALY CITY':  'bart-daly-city',
  'RICHMOND':   'bart-richmond',
  'VALLEJO':    'bart-el-cerrito-del-norte',
};

const HUB_STATIONS = [
  'bart-12th-st',
  'bart-powell',
  'caltrain-diridon',
  'bart-millbrae',
  'bart-embarcadero',
  'bart-fruitvale',
];

// Editorial labels for the six hubs (Zora ruling 2026-07-15, addendum §3 HUB
// LABELS — supersedes displayStationName ON THE VENUEPAGE SURFACE only).
// ADDITIVE sibling of HUB_STATIONS: keys are the existing station ids (the
// hub-preservation invariant + build-transit's required-id gate are untouched).
// The hubs are an editorial set; their labels are editorial too — rider
// vernacular, not feed strings (a first-N-words transform was rejected: it
// encodes feed-name-shape assumptions that break on refresh). Stored title
// case; the consuming surface uppercases at render. ENFORCEMENT: a unit test
// asserts every HUB_STATIONS id has an entry here, so a future 7th hub can
// never ship label-less.
const HUB_LABELS = {
  'bart-12th-st':     '12th St',
  'bart-powell':      'Powell',
  'caltrain-diridon': 'Diridon',
  'bart-millbrae':    'Millbrae',
  'bart-embarcadero': 'Embarcadero',
  'bart-fruitvale':   'Fruitvale',
};

// Canonical transit-system colors — single source of truth for BOTH the line
// renderer (FlowsTransitMap) and the legend/toggles. Previously BART's toggle
// was orange (#FF6319) while its line was blue (#0099D8); unified here so the
// legend always matches the map. Muni moved off its muddy near-Caltrain red to
// eucalyptus so all three systems read distinctly on the dark map.
const SYSTEM_INFO = {
  bart:     { label: 'BART',     color: '#16A6E6' },
  caltrain: { label: 'Caltrain', color: '#E31837' },
  muni:     { label: 'Muni',     color: '#6CA085' },
  // VTA joins the lens (Phase 1). Magenta is the only free hue family on the
  // page — Caltrain owns red, Muni green, BART cyan-blue, and orange/amber are
  // reserved for the reach ramp (spec §0.5). Toggles + dots use this canonical
  // hex; light-mode LINE strokes use a darker ink mapped in the renderer.
  vta:      { label: 'VTA',      color: '#C0508A' },
};

// ---------------------------------------------------------------------------
// displayStationName(station) → clean human label (spec §7, pure display —
// NEVER mutates data). Strips the display noise the feed exposes verbatim:
//   - trailing " BART" ONLY when system !== 'bart' (co-location marker on a
//     Caltrain/VTA platform, e.g. "Millbrae BART" caltrain → "Millbrae")
//   - trailing " Station" ("Alder Station" → "Alder")
//   - trailing direction tokens ("4th & Brannan Southbound" → "4th & Brannan")
// Applied to a fixpoint so compound suffixes collapse ("… Station Southbound").
// ---------------------------------------------------------------------------
function displayStationName(station, opts) {
  if (!station) return '';
  if (!station.name) return station.id || '';
  // §7 default: strip trailing " BART" only when the station is NOT itself a
  // BART stop (a Caltrain/VTA platform co-located at a BART station, e.g.
  // "Millbrae BART" caltrain → "Millbrae"). B9 (§7 amendment): a caller
  // rendering a name INSIDE a bart-line context — the route toward-label, where
  // the step already reads "BART RED" — passes { stripBart:true } so the
  // redundant "MILLBRAE BART" collapses to "MILLBRAE". One transform, no
  // hand-map; the flag only ever ADDS the strip (never restores it).
  const stripBart = (opts && opts.stripBart) || station.system !== 'bart';
  let n = String(station.name).trim();
  let prev;
  do {
    prev = n;
    if (stripBart) n = n.replace(/\s+BART$/, '');
    n = n.replace(/\s+Station$/i, '');
    n = n.replace(/[\s/]+(Inbound|Outbound|Northbound|Southbound)$/i, '');
    n = n.trim();
  } while (n !== prev && n.length > 0);
  return n.length > 0 ? n : String(station.name);
}

// ---------------------------------------------------------------------------
// collapseTwins(stations) → deduped station[] (spec §7). Collapses stations
// sharing (system, displayStationName) AND within 160 m of each other into one
// entry; canonical = lexicographically-first id. Real scale (B10-1 audit,
// 2026-07-20): 3 same-name clusters live — 2 Muni direction-twin pairs + one
// "-2" dup — not the "65 pairs" this comment once claimed; the name key also
// structurally MISSES 4th & Brannan ("Fourth Street & Brannan" never matches
// "4th & Brannan" after direction-strip). B10-2: search no longer calls this —
// every surface now dedupes on the data-layer canonicalId (stations.json,
// emitted by scrapers/build-transit.js) — but the function stays as a lib/test
// surface and the Block-1 parity anchor (tests/transit-canonical-id.test.js
// pins canonical clusters ≡ these clusters ∪ the Brannan allowlist). Returns
// the canonical station objects in stable (input) order. Distinct-name /
// far-apart stations pass through untouched.
// ---------------------------------------------------------------------------
function collapseTwins(stations) {
  const list = Array.isArray(stations) ? stations : [];
  const TWIN_METERS = 160;
  const groups = new Map(); // key → station[]
  for (const s of list) {
    if (!s || s.lat == null || s.lng == null) continue;
    const key = (s.system || '') + '|' + displayStationName(s).toLowerCase();
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  }
  const canonicalIds = new Set();
  for (const members of groups.values()) {
    const unassigned = members.slice();
    while (unassigned.length) {
      const seed = unassigned.shift();
      const cluster = [seed];
      for (let i = unassigned.length - 1; i >= 0; i--) {
        const other = unassigned[i];
        const meters = haversine(seed.lat, seed.lng, other.lat, other.lng) * 1609.344;
        if (meters <= TWIN_METERS) {
          cluster.push(other);
          unassigned.splice(i, 1);
        }
      }
      let canonical = cluster[0].id;
      for (const c of cluster) if (String(c.id) < String(canonical)) canonical = c.id;
      canonicalIds.add(canonical);
    }
  }
  // Preserve input order; emit only canonicals (+ any station that lacked
  // coords, so nothing silently vanishes from search).
  return list.filter(s => s && (canonicalIds.has(s.id) || s.lat == null || s.lng == null));
}

// ---------------------------------------------------------------------------
// bandForMinutes(t) → 'reach1'|'reach2'|'reach3'|'reach4' (spec §0.5/§4.4).
// Bands at 20/40/60. Rounds first so the band always matches the rendered
// "~X min" number (a t=20.6 shows "~21 min" and must land in reach2).
// ---------------------------------------------------------------------------
function bandForMinutes(t) {
  const m = Math.round(typeof t === 'number' ? t : NaN);
  if (!isFinite(m)) return null;
  if (m <= 20) return 'reach1';
  if (m <= 40) return 'reach2';
  if (m <= 60) return 'reach3';
  return 'reach4';
}

// ---------------------------------------------------------------------------
// bandForHour(hour) → headway band for an hour-of-day (0–23). THE single
// source for the band boundaries: am_peak 06–10, midday 10–15, pm_peak 15–18,
// evening 18–22, else late (covers 22:00–06:00). currentBand() derives its
// band from this; the P1 date-aware headway path calls it directly with a
// linked EVENT's start hour so a future-day row reads that day's band —
// never tonight's — from the same boundaries.
// ---------------------------------------------------------------------------
function bandForHour(hour) {
  const h = typeof hour === 'number' && isFinite(hour) ? ((Math.floor(hour) % 24) + 24) % 24 : null;
  if (h === null) return null;
  if (h >= 6 && h < 10) return 'am_peak';
  if (h >= 10 && h < 15) return 'midday';
  if (h >= 15 && h < 18) return 'pm_peak';
  if (h >= 18 && h < 22) return 'evening';
  return 'late';
}

// ---------------------------------------------------------------------------
// currentBand(date?) → { band, dayType } (spec §3.3). Pacific clock ALWAYS —
// never d.getHours() on a parsed ISO (feedback_pacific_time_in_scrapers).
// Band boundaries live in bandForHour (single source). dayType by Pacific
// weekday.
// ---------------------------------------------------------------------------
function currentBand(date) {
  const d = (date instanceof Date && !isNaN(date.getTime())) ? date : new Date();
  let hour, wd;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles', hour12: false, hour: '2-digit', weekday: 'short',
    }).formatToParts(d);
    for (const p of parts) {
      if (p.type === 'hour') hour = parseInt(p.value, 10) % 24;
      else if (p.type === 'weekday') wd = p.value;
    }
  } catch (e) {
    hour = d.getHours();
    wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  }
  const dayType = wd === 'Sat' ? 'saturday' : wd === 'Sun' ? 'sunday' : 'weekday';
  // Unparseable hour degrades to 'late' — the pre-extraction fallthrough.
  return { band: bandForHour(hour) || 'late', dayType: dayType };
}

// rawToMinutes("26:10:00") → 1570 (minutes since midnight; 24h+ NOT folded, so
// past-midnight departures compare correctly against an evening event start).
function rawToMinutes(raw) {
  if (typeof raw !== 'string') return null;
  const m = raw.match(/(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

// lineDisplayName("VTA Blue Line") → "VTA BLUE" (uppercase, drop trailing LINE).
function lineDisplayName(name) {
  if (!name) return '';
  return String(name).toUpperCase().replace(/\s+LINE$/, '').trim();
}

// ---------------------------------------------------------------------------
// holidaySchedule(exceptions, ymd, system) → boolean (P2 fix, 2026-08-26).
// TRUE only when a calendar exception REMOVES service for `system` on `ymd`
// AND that date is a WEEKDAY (Mon–Fri). The pre-P2 rule badged on ANY
// matching exception, and the shipped meta.exceptions (data/last-train.json,
// from GTFS calendar_dates) carries three species that must not be conflated:
//
//   type:'added'            — supplementary service (Muni SF:82660 on every
//     weekday of the horizon, the Caltrain June specials). MORE service is
//     not a holiday; badging it told riders a plain Friday ran reduced.
//   type:'removed' Sat/Sun  — BART encodes its REGULAR weekend calendar as
//     per-date removals of weekday service (BA:72953-5 / BA:75396-7 alternate
//     across every Sat/Sun of the horizon), so every normal weekend badged.
//     Also the one-off Muni change weekend (2026-07-25/26) — a real service
//     change, but "HOLIDAY SCHED" is the only sanctioned copy for this badge,
//     so weekend removals never badge (a SERVICE CHANGE badge is future work
//     needing Rhea copy).
//   type:'removed' weekday  — every observed instance is a true holiday
//     (Presidents/Memorial/Labor Day, Thanksgiving + the Friday after,
//     12-24, 12-25, 01-01): weekday service pulled, Sunday service added in
//     its place. THIS is the badge.
//
// The weekday comes from the matched date itself — pure calendar math on the
// YYYYMMDD literal via Date.UTC (a calendar date's weekday is timezone-free;
// feedback_pacific_time_in_scrapers governs CLOCK parsing, which this is
// not). Positive evidence only: an unparseable ymd or a typeless entry never
// badges — the pre-P2 bug was badging on mere presence.
// ---------------------------------------------------------------------------
function holidaySchedule(exceptions, ymd, system) {
  if (!Array.isArray(exceptions) || typeof ymd !== 'string') return false;
  const m = ymd.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (!m) return false;
  const dow = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay();
  if (dow === 0 || dow === 6) return false;
  return exceptions.some(e => e && e.date === ymd && e.system === system && e.type === 'removed');
}

// reconstructHops(fromId, toId, prev) → [{ from, to, edge }] forward order,
// or [] if unreachable. Cycle-guarded.
function reconstructHops(fromId, toId, prev) {
  if (fromId === toId) return [];
  const get = (id) => (prev && prev.get ? prev.get(id) : (prev ? prev[id] : null));
  const hops = [];
  const seen = new Set();
  let cur = toId;
  while (cur !== fromId) {
    const p = get(cur);
    if (!p || seen.has(cur)) return [];
    seen.add(cur);
    hops.unshift({ from: p.from, to: cur, edge: p.edge });
    cur = p.from;
  }
  return hops;
}

// ---------------------------------------------------------------------------
// buildRouteSteps(fromId, toId, prev, stations, lines) → step[] (spec §5.1).
// Returns route DATA (T10 renders the glyphs/chips + appends the arrival step).
//   ride:     { kind:'ride', lineId, lineName, lineColor, towardId, towardName, stops, minutes }
//   transfer: { kind:'transfer', atId, atName, minutes }
// Consecutive ride edges sharing a common line collapse into one step. When
// several lines serve a run (BART trunk), pick the line that also serves the
// next run (fewest transfers); tie → alphabetical by line id. Direction word =
// display name of the run's far endpoint. `lines` = lines.json FeatureCollection.
// ---------------------------------------------------------------------------
function buildRouteSteps(fromId, toId, prev, stations, lines) {
  const hops = reconstructHops(fromId, toId, prev);
  if (hops.length === 0) return [];

  const feats = lines && lines.features ? lines.features : (Array.isArray(lines) ? lines : []);
  const lineById = {};
  for (const f of feats) { const p = (f && f.properties) || f; if (p && p.id) lineById[p.id] = p; }
  const stById = {};
  for (const s of (stations || [])) if (s) stById[s.id] = s;
  const nameOf = (id) => (stById[id] ? displayStationName(stById[id]) : id);

  // Pass 1 — group hops into runs (ride segments split by transfer edges, and
  // sub-split where no single line spans the whole run).
  const runs = [];
  let i = 0;
  while (i < hops.length) {
    const h = hops[i];
    if (h.edge && h.edge.type === 'transfer') {
      runs.push({ type: 'transfer', hops: [h] });
      i++;
      continue;
    }
    let inter = new Set(h.edge && h.edge.lines ? h.edge.lines : []);
    const runHops = [h];
    let j = i + 1;
    while (j < hops.length) {
      const hj = hops[j];
      if (hj.edge && hj.edge.type === 'transfer') break;
      const next = new Set();
      for (const l of (hj.edge && hj.edge.lines ? hj.edge.lines : [])) if (inter.has(l)) next.add(l);
      if (next.size === 0) break;
      inter = next;
      runHops.push(hj);
      j++;
    }
    runs.push({ type: 'ride', hops: runHops, lineSet: inter });
    i = j;
  }

  // Pass 2 — emit steps, choosing lines with look-ahead to the next ride run.
  const steps = [];
  for (let r = 0; r < runs.length; r++) {
    const run = runs[r];
    if (run.type === 'transfer') {
      const h = run.hops[0];
      steps.push({ kind: 'transfer', atId: h.to, atName: nameOf(h.to), minutes: h.edge.time });
      continue;
    }
    const candidates = Array.from(run.lineSet);
    let chosen = null;
    let nextRide = null;
    for (let k = r + 1; k < runs.length; k++) { if (runs[k].type === 'ride') { nextRide = runs[k]; break; } }
    if (nextRide) {
      const shared = candidates.filter(l => nextRide.lineSet.has(l));
      if (shared.length) chosen = shared.sort()[0];
    }
    if (!chosen) chosen = candidates.sort()[0] || null;
    const lastHop = run.hops[run.hops.length - 1];
    const feat = chosen ? lineById[chosen] : null;
    // B9 (§7 amendment): on a BART-line step the "BART" suffix on the terminal
    // station name is redundant with the "BART …" line label — strip it. The
    // line system is the id prefix ("bart-red" → "bart"); non-bart runs use the
    // default transform (which already strips " BART" from co-located platforms).
    const chosenSystem = chosen ? String(chosen).split('-')[0] : '';
    const towardStation = stById[lastHop.to];
    const towardName = towardStation
      ? displayStationName(towardStation, { stripBart: chosenSystem === 'bart' })
      : lastHop.to;
    steps.push({
      kind: 'ride',
      lineId: chosen,
      lineName: feat ? lineDisplayName(feat.name) : (chosen ? chosen.toUpperCase() : ''),
      lineColor: feat ? feat.color : null,
      fromId: run.hops[0].from, // boarding station — with towardId, the leg lib/flows-fares.js prices
      towardId: lastHop.to,
      towardName: towardName,
      stops: run.hops.reduce((a, h) => a + (h.edge.stops || 0), 0),
      minutes: run.hops.reduce((a, h) => a + (h.edge.time || 0), 0),
    });
  }
  return steps;
}

// ---------------------------------------------------------------------------
// lastTrainTier(eventStartMin, line, dayType, lastTrainData, opts?) (spec §5.3,
// amended by P1 2026-07-16: state the fact, drop the judgment — Reza's ruling)
// Returns the tier classification + the data T10 needs to stamp a chip. Pure —
// no string assembly (clock formatting lives in lib/format-reach-time.js).
//   eventStartMin: event start, minutes since midnight
//   line:          return route's first-leg line id ("bart-blue", "muni-n")
//   dayType:       'weekday'|'saturday'|'sunday'
//   opts.todayYmd: "YYYYMMDD" → sets result.holiday via holidaySchedule (P2:
//                  only a weekday whose service is REMOVED by an exception —
//                  added service and weekend calendar-encoding never badge)
// Conservative rule (§5.2): L = the EARLIER of the line's two direction clocks
// (never overpromises). Owl override: after-last on a Muni Owl corridor line
// becomes a way home.
//
// P1 — WHY THERE IS NO GRADED TIER (and why you must not add one back):
// the ladder once graded L against the event's START plus a duration guess.
// But S is optimistic by construction — the shipped dataset is hour-floored
// (2 of 1455 rows carry minutes) and sources publish doors-vs-showtime
// ambiguity we don't disambiguate — so every graded read skewed optimistic,
// hardest at exactly the small rooms the site exists for. Ruling (d): the
// ONLY judgments we keep are the ones no duration model can change —
// no service (L === null) and "the last train leaves before the show even
// STARTS" (L < S). Everything else is 'fact': state the clock, no grade.
// A future graded ladder is gated behind trustworthy S (P2 minutes + a
// doors-vs-showtime audit) and NAMED terms (E = S + D, G = egress) — see
// docs/design/2026-07-16-fable5-P1-cut-the-tier-to-facts-prompt.md. The
// vocabulary test in tests/flows-fact-tier.test.js fails on any new tier.
// ---------------------------------------------------------------------------
function lastTrainTier(eventStartMin, line, dayType, lastTrainData, opts) {
  opts = opts || {};
  const system = (line || '').split('-')[0] || '';
  const sysLabel = (SYSTEM_INFO[system] && SYSTEM_INFO[system].label) || system.toUpperCase();
  const deps = (lastTrainData && lastTrainData.lastDepartures) || [];

  let L = null, clockRaw = null, chosenRow = null;
  for (const row of deps) {
    if (row.line === line && row.dayType === dayType && row.lastDeparture) {
      const mins = rawToMinutes(row.lastDeparture.raw);
      if (mins != null && (L === null || mins < L)) { L = mins; clockRaw = row.lastDeparture.raw; chosenRow = row; }
    }
  }

  const owlCorridors = (lastTrainData && lastTrainData.meta && lastTrainData.meta.nightService
    && lastTrainData.meta.nightService.muniOwl && lastTrainData.meta.nightService.muniOwl.corridors) || [];
  const owl = owlCorridors.find(c => c.line === line) || null;

  let holiday = false;
  if (opts.todayYmd && lastTrainData && lastTrainData.meta && Array.isArray(lastTrainData.meta.exceptions)) {
    holiday = holidaySchedule(lastTrainData.meta.exceptions, opts.todayYmd, system);
  }

  const S = eventStartMin;
  const startKnown = typeof S === 'number' && isFinite(S);
  let tier;
  if (L === null) tier = 'after-last';           // no service for this line today — a fact
  // B7 (§5.2 "always when home set"): the return line HAS a last departure but
  // the event start is unparseable → the clock is still a fact. 'neutral'
  // renders the origin-anchored GETTING BACK line with no tier color instead
  // of omitting it. Only the route panel relaxes its start guard to reach
  // this; the feed-row chip stays start-gated (no stamp).
  else if (!startKnown) tier = 'neutral';
  // P1: the last train leaves BEFORE the show starts — no duration model can
  // rescue that; it is a fact, not an estimate. Everything at-or-after start
  // is 'fact': the GTFS-exact clock renders, nothing gets graded.
  else if (L < S) tier = 'after-last';
  else tier = 'fact';

  let glyph = '';
  if (tier === 'after-last' && owl) { tier = 'owl-override'; glyph = '☽'; }   // ☽
  else if (tier === 'after-last') glyph = '⊘';                               // ⊘

  return {
    tier: tier,
    system: system,
    systemLabel: sysLabel,
    lastDepartureMin: L,
    clockRaw: clockRaw,
    glyph: glyph,
    holiday: holiday,
    owlLetter: owl ? owl.letter : null,
    owlLine: owl ? owl.line : null,
    // T10 GETTING BACK (§5.2): the headsign + origin of the conservative
    // (earliest) departure row. Goal 4: the display line is HOME-anchored now
    // ("LAST TRAIN HOME TO {home}"); originId feeds the honesty sub-line
    // ("leaves {origin}, the start of the line") and headsign stays an
    // additive data field (no display consumer today). Null when the line
    // has no service today.
    headsign: chosenRow && chosenRow.direction ? chosenRow.direction.headsign : null,
    originId: chosenRow ? chosenRow.origin : null,
  };
}

// ---------------------------------------------------------------------------
// Window exports
// ---------------------------------------------------------------------------
window.haversine             = haversine;
window.findNearestStation    = findNearestStation;
window.dijkstra              = dijkstra;
window.dijkstraWithRoutes    = dijkstraWithRoutes;
window.buildRouteSteps       = buildRouteSteps;
window.displayStationName    = displayStationName;
window.collapseTwins         = collapseTwins;
window.bandForMinutes        = bandForMinutes;
window.bandForHour           = bandForHour;
window.currentBand           = currentBand;
window.holidaySchedule       = holidaySchedule;
window.lastTrainTier         = lastTrainTier;
window.matchEventsToStations = matchEventsToStations;
window.STATION_MATCH_MILES   = STATION_MATCH_MILES;
window.CITY_HUBS             = CITY_HUBS;
window.HUB_STATIONS          = HUB_STATIONS;
window.HUB_LABELS            = HUB_LABELS;
window.SYSTEM_INFO           = SYSTEM_INFO;
